// ConfirmScreen.jsx
// Final review + optional 2FA before submitting.
// Used by both balance-transfer and T2A paths.

import React from "react";
import {
  Box, Paper, Typography, Stack, Button, Divider, Alert, CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CardGiftcard as GiftIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { fmtUSD, PAYMENT_RAILS } from "./T2a.utils";

/**
 * Props
 * ─────
 * recipients      : Recipient[]
 * totalAmount     : number
 * description     : string
 * origin          : "balance" | "pay_now"
 * rail            : "paypal" | "stripe"
 * feePayer        : "sender" | "recipient"
 * calc            : { senderPays, recipientReceives, fee }
 * balance         : number
 * isLoading       : boolean
 * requires2FA     : boolean
 * twoFAProps      : { otpCode, setOtpCode, otpMethod, setOtpMethod, otpSent, otpSending, otpError, setOtpError, otpCountdown, resendCooldown, sendOtp }
 * onBack          : () => void
 * onConfirm       : () => void
 */
export function ConfirmScreen({
  recipients, totalAmount, description,
  origin, rail, feePayer, calc,
  balance, isLoading, requires2FA, twoFAProps,
  onBack, onConfirm,
}) {
  const {
    otpCode, setOtpCode,
    otpMethod, setOtpMethod,
    otpSending, otpError, setOtpError,
    otpCountdown, resendCooldown,
    sendOtp,
  } = twoFAProps;

  const isT2A      = origin === "pay_now";
  const railLabel  = PAYMENT_RAILS[rail]?.label || rail;
  const canSubmit  = !isLoading && (!requires2FA || otpCode.length === 6);

  return (
    <Box>
      {/* Back + title */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={isLoading}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          size="small"
        >
          Back
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {requires2FA ? "Verify transfer" : isT2A ? "Confirm T2A transfer" : "Confirm transfer"}
        </Typography>
        <Box sx={{ width: 64 }} />
      </Box>

      {/* Summary card */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack spacing={1.25}>
          {/* Recipients */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              {recipients.length === 1 ? "Recipient" : "Recipients"}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {recipients.length === 1
                ? recipients[0].label
                : `${recipients.length} people`}
            </Typography>
          </Box>

          {/* Agreed total */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">Agreed amount</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtUSD(totalAmount)}</Typography>
          </Box>

          {/* T2A-specific rows */}
          {isT2A && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Payment method</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{railLabel}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Fee paid by</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{feePayer === "sender" ? "You" : "Recipient(s)"}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Processing fee</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "error.main" }}>−{fmtUSD(calc.fee)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Recipient(s) receive</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>
                  {fmtUSD(feePayer === "sender" ? totalAmount : calc.recipientReceives)}
                </Typography>
              </Box>
            </>
          )}

          {/* Balance-transfer specific rows */}
          {!isT2A && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Transfer fee</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>Free</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Balance after</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtUSD(balance - totalAmount)}</Typography>
              </Box>
            </>
          )}

          <Divider />

          {/* You pay */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>You pay</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: "1rem", color: "primary.main" }}>
              {isT2A ? fmtUSD(calc.senderPays) : fmtUSD(totalAmount)}
            </Typography>
          </Box>

          {/* Description */}
          {description && (
            <Box>
              <Typography variant="caption" color="text.secondary">Note</Typography>
              <Typography variant="body2">{description}</Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Per-recipient breakdown when multiple */}
      {recipients.length > 1 && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, maxHeight: 200, overflowY: "auto" }}>
          <Stack spacing={0.5}>
            {recipients.map((r) => (
              <Box key={r.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" noWrap sx={{ maxWidth: 220 }}>{r.label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{fmtUSD(parseFloat(r.amount) || 0)}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* 2FA block */}
      {requires2FA && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, borderColor: "warning.300", bgcolor: "warning.50" }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Verification required</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Enter the 6-digit code sent to your {otpMethod === "sms" ? "phone" : "email"}
          </Typography>
          <Box
            component="input"
            type="text"
            placeholder="000000"
            value={otpCode}
            onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setOtpCode(v); setOtpError(""); }}
            maxLength={6}
            disabled={isLoading}
            sx={{
              width: "100%", p: 1.5, fontSize: "22px", textAlign: "center", letterSpacing: "10px",
              border: "2px solid", borderColor: otpError ? "error.main" : "divider", borderRadius: 1,
              outline: "none", fontWeight: 600, fontFamily: "inherit", boxSizing: "border-box",
              bgcolor: "white",
            }}
          />
          {otpError && (
            <Typography variant="caption" color="error" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
              {otpError}
            </Typography>
          )}
          {otpCountdown > 0 && (
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 0.5, fontWeight: 600 }}>
              Expires in {Math.floor(otpCountdown / 60)}:{(otpCountdown % 60).toString().padStart(2, "0")}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
            <Button size="small" disabled={otpSending || resendCooldown > 0} onClick={() => sendOtp(otpMethod, totalAmount)} sx={{ textTransform: "none" }}>
              {otpSending ? "Sending…" : resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend"}
            </Button>
            <Button size="small" disabled={otpSending} onClick={() => { const m = otpMethod === "email" ? "sms" : "email"; setOtpMethod(m); sendOtp(m, totalAmount); }} sx={{ textTransform: "none" }}>
              Use {otpMethod === "email" ? "SMS" : "email"} instead
            </Button>
          </Box>
        </Paper>
      )}

      <Alert severity="warning" sx={{ borderRadius: 2, mb: 2, py: 0.75 }}>
        <Typography variant="caption">This transfer cannot be reversed once sent.</Typography>
      </Alert>

      {/* Actions */}
      <Stack direction="row" spacing={1}>
        <Button fullWidth variant="outlined" onClick={onBack} disabled={isLoading} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={onConfirm}
          disabled={!canSubmit}
          startIcon={
            isLoading
              ? <CircularProgress size={16} />
              : isT2A ? <GiftIcon /> : <SendIcon />
          }
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {isLoading
            ? "Sending…"
            : isT2A
              ? origin === "pay_now" ? "Confirm & pay" : "Confirm & send"
              : "Send transfers"}
        </Button>
      </Stack>
    </Box>
  );
}